import { useState, useEffect, useRef } from "react"
import { MapPin, Navigation } from "lucide-react"

function LocationPicker({ value = {}, onChange, className = "" }) {
  const [loading, setLoading] = useState(false)
  const [postalLoading, setPostalLoading] = useState(false)
  const [geocodingLoading, setGeocodingLoading] = useState(false)
  const [countries, setCountries] = useState([])
  const [regions, setRegions] = useState([])
  const [cities, setCities] = useState([])
  const [isInitializing, setIsInitializing] = useState(true)
  const [formData, setFormData] = useState({
    country_code: value.country_code || "",
    country_name: value.country_name || "",
    region_code: value.region_code || "",
    region_name: value.region_name || "",
    city_locality: value.city_locality || "",
    city_sublocality: value.city_sublocality || "",
    postal_code: value.postal_code || "",
    latitude: value.latitude || null,
    longitude: value.longitude || null,
    city_place_id: value.city_place_id || "",
    location_source: value.location_source || "",
    location_confidence: value.location_confidence || 0,
  })

  // Debounced update ref
  const updateTimeoutRef = useRef(null)

  // Google Maps API key - set REACT_APP_GOOGLE_MAPS_API_KEY in .env (required for geocoding)
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  // Cleanup on unmount
  useEffect(() => {
    loadCountries()
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [])

  // Sync formData with value prop changes
  useEffect(() => {
    setIsInitializing(true)
    setFormData({
      country_code: value.country_code || "",
      country_name: value.country_name || "",
      region_code: value.region_code || "",
      region_name: value.region_name || "",
      city_locality: value.city_locality || "",
      city_sublocality: value.city_sublocality || "",
      postal_code: value.postal_code || "",
      latitude: value.latitude || null,
      longitude: value.longitude || null,
      city_place_id: value.city_place_id || "",
      location_source: value.location_source || "",
      location_confidence: value.location_confidence || 0,
    })
    // Allow onChange to fire after initialization
    setTimeout(() => setIsInitializing(false), 0)
  }, [value])

  // Update regions when country changes
  useEffect(() => {
    if (formData.country_code) {
      loadRegions(formData.country_code)
    } else {
      setRegions([])
      setCities([])
    }
  }, [formData.country_code])

  // Update cities when region changes
  useEffect(() => {
    if (formData.country_code && formData.region_code) {
      loadCities(formData.country_code, formData.region_code)
    } else {
      setCities([])
    }
  }, [formData.country_code, formData.region_code])

  // Notify parent of changes with debouncing
  useEffect(() => {
    if (onChange && !isInitializing && !geocodingLoading) {
      // Clear existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }

      // Debounce the update to prevent excessive calls
      updateTimeoutRef.current = setTimeout(() => {
        onChange({
          ...formData,
          location_updated_at: new Date().toISOString(),
        })
      }, 150) // 150ms debounce
    }

    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [formData, onChange, isInitializing, geocodingLoading])

  const loadCountries = async () => {
    try {
      // For now, load basic countries. In production, you might want to use a countries API
      const basicCountries = [
        { code: "CA", name: "Canada" },
        { code: "US", name: "United States" },
        { code: "GB", name: "United Kingdom" },
        { code: "AU", name: "Australia" },
        { code: "DE", name: "Germany" },
        { code: "FR", name: "France" },
        { code: "JP", name: "Japan" },
      ]
      setCountries(basicCountries)
    } catch (error) {
      console.error("Error loading countries:", error)
    }
  }

  const loadRegions = async (countryCode) => {
    try {
      // This would typically call your backend or a regions API
      // For now, using basic regions for Canada and US
      const regionData = {
        CA: [
          { code: "ON", name: "Ontario" },
          { code: "QC", name: "Quebec" },
          { code: "BC", name: "British Columbia" },
          { code: "AB", name: "Alberta" },
          { code: "MB", name: "Manitoba" },
          { code: "SK", name: "Saskatchewan" },
          { code: "NS", name: "Nova Scotia" },
          { code: "NB", name: "New Brunswick" },
          { code: "NL", name: "Newfoundland and Labrador" },
          { code: "PE", name: "Prince Edward Island" },
          { code: "NT", name: "Northwest Territories" },
          { code: "NU", name: "Nunavut" },
          { code: "YT", name: "Yukon" },
        ],
        US: [
          { code: "CA", name: "California" },
          { code: "NY", name: "New York" },
          { code: "TX", name: "Texas" },
          { code: "FL", name: "Florida" },
          { code: "IL", name: "Illinois" },
          { code: "PA", name: "Pennsylvania" },
          { code: "OH", name: "Ohio" },
          { code: "GA", name: "Georgia" },
          { code: "NC", name: "North Carolina" },
          { code: "MI", name: "Michigan" },
        ],
      }

      setRegions(regionData[countryCode] || [])
    } catch (error) {
      console.error("Error loading regions:", error)
    }
  }

  const loadCities = async (countryCode, regionCode) => {
    try {
      // This would typically call your backend or a cities API
      // For now, using basic cities for Ontario, CA
      const cityData = {
        "CA-ON": [
          { name: "Toronto" },
          { name: "Ottawa" },
          { name: "Mississauga" },
          { name: "Brampton" },
          { name: "Hamilton" },
          { name: "London" },
          { name: "Markham" },
          { name: "Vaughan" },
          { name: "Kitchener" },
          { name: "Windsor" },
        ],
        "US-CA": [
          { name: "Los Angeles" },
          { name: "San Francisco" },
          { name: "San Diego" },
          { name: "San Jose" },
          { name: "Sacramento" },
          { name: "Fresno" },
          { name: "Long Beach" },
          { name: "Oakland" },
          { name: "Bakersfield" },
          { name: "Anaheim" },
        ],
      }

      const key = `${countryCode}-${regionCode}`
      setCities(cityData[key] || [])
    } catch (error) {
      console.error("Error loading cities:", error)
    }
  }

  const handleLocateMe = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.")
      return
    }

    setLoading(true)
    setGeocodingLoading(true)
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        })
      })

      const { latitude, longitude } = position.coords

      // Reverse geocode using Google Geocoding API
      await reverseGeocode(latitude, longitude, "geolocation")

    } catch (error) {
      console.error("Error getting location:", error)
      alert("Unable to get your location. Please enter your address manually.")
    } finally {
      setLoading(false)
      setGeocodingLoading(false)
    }
  }

  const handlePostalCodeChange = async (postalCode) => {
    setFormData(prev => ({ ...prev, postal_code: postalCode }))

    if (postalCode.length >= 3) {
      if (!GOOGLE_API_KEY) {
        console.warn('REACT_APP_GOOGLE_MAPS_API_KEY not set; postal code lookup disabled.')
        return
      }
      setPostalLoading(true)
      setGeocodingLoading(true)
      try {
        // Use Google Geocoding API to get location from postal code
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(postalCode)}&key=${GOOGLE_API_KEY}`
        )

        const data = await response.json()

        if (data.results && data.results.length > 0) {
          const result = data.results[0]
          const lat = result.geometry.location.lat
          const lng = result.geometry.location.lng

          await reverseGeocode(lat, lng, "postal_code", result.place_id)
        }
      } catch (error) {
        console.error("Error geocoding postal code:", error)
      } finally {
        setPostalLoading(false)
        setGeocodingLoading(false)
      }
    }
  }

  const reverseGeocode = async (lat, lng, source, placeId = null) => {
    if (!GOOGLE_API_KEY) {
      console.warn('REACT_APP_GOOGLE_MAPS_API_KEY not set; reverse geocoding disabled.')
      return
    }
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
      )

      const data = await response.json()

      if (data.results && data.results.length > 0) {
        const result = data.results[0]
        const addressComponents = result.address_components

        // Parse address components
        const parsed = parseAddressComponents(addressComponents)

        // Batch update all form data at once to prevent multiple re-renders
        setFormData(prev => ({
          ...prev,
          ...parsed,
          latitude: lat,
          longitude: lng,
          city_place_id: placeId || result.place_id,
          location_source: source,
          location_confidence: Math.round(result.geometry.location_type === "ROOFTOP" ? 100 :
            result.geometry.location_type === "RANGE_INTERPOLATED" ? 75 :
            result.geometry.location_type === "GEOMETRIC_CENTER" ? 50 : 25),
        }))

        // Update dependent dropdowns after a brief delay to allow formData to settle
        setTimeout(async () => {
          if (parsed.country_code) {
            await loadRegions(parsed.country_code)
            if (parsed.region_code) {
              await loadCities(parsed.country_code, parsed.region_code)
            }
          }
        }, 50)
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error)
    }
  }

  const parseAddressComponents = (components) => {
    const parsed = {
      country_code: "",
      country_name: "",
      region_code: "",
      region_name: "",
      city_locality: "",
      city_sublocality: "",
      postal_code: "",
    }

    components.forEach(component => {
      const types = component.types

      if (types.includes("country")) {
        parsed.country_code = component.short_name
        parsed.country_name = component.long_name
      } else if (types.includes("administrative_area_level_1")) {
        parsed.region_code = component.short_name
        parsed.region_name = component.long_name
      } else if (types.includes("locality")) {
        parsed.city_locality = component.long_name
      } else if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
        parsed.city_sublocality = component.long_name
      } else if (types.includes("postal_code")) {
        parsed.postal_code = component.long_name
      } else if (types.includes("postal_code_prefix")) {
        parsed.postal_code = component.long_name
      }
    })

    return parsed
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear dependent fields when parent changes
    if (field === "country_code") {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        region_code: "",
        region_name: "",
        city_locality: "",
        city_sublocality: "",
      }))
    } else if (field === "region_code") {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        city_locality: "",
        city_sublocality: "",
      }))
    }
  }

  return (
    <div className={`space-y-4 ${className} relative`}>
      {/* Loading overlay during geocoding */}
      {geocodingLoading && (
        <div className="absolute inset-0 bg-dark/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple mx-auto mb-2"></div>
            <p className="text-cream text-sm">Finding your location...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <h3 className="text-cream text-lg font-semibold flex items-center justify-center gap-2">
          <MapPin size={20} className="text-purple" />
          Location
        </h3>
        <p className="text-cream/70 text-sm mt-1">
          To find listings near you
        </p>
      </div>

      {/* Locate Me Button */}
      <button
        type="button"
        onClick={handleLocateMe}
        disabled={loading}
        className="w-full bg-purple/10 hover:bg-purple/20 text-purple border border-purple/30 rounded-lg py-3 px-4 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Navigation size={18} />
        {loading ? "Getting your location..." : "Locate Me"}
      </button>

      {/* Postal Code */}
      <div className="space-y-2">
        <label className="text-cream text-sm block">
          Postal Code <span className="text-cream/50">(optional)</span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={formData.postal_code}
            onChange={(e) => handlePostalCodeChange(e.target.value)}
            placeholder="Enter postal code"
            className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
          />
          {postalLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple"></div>
            </div>
          )}
        </div>
      </div>

      {/* Country Select */}
      <div className="space-y-2">
        <label className="text-cream text-sm block">
          Country
        </label>
        <select
          value={formData.country_code}
          onChange={(e) => handleInputChange("country_code", e.target.value)}
          className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple"
        >
          <option value="">Select Country</option>
          {countries.map(country => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>

      {/* Region Select */}
      {formData.country_code && (
        <div className="space-y-2">
          <label className="text-cream text-sm block">
            {formData.country_code === "US" ? "State" : "Province"}
          </label>
          <select
            value={formData.region_code}
            onChange={(e) => handleInputChange("region_code", e.target.value)}
            className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple"
          >
            <option value="">Select {formData.country_code === "US" ? "State" : "Province"}</option>
            {regions.map(region => (
              <option key={region.code} value={region.code}>
                {region.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* City Select */}
      {formData.country_code && formData.region_code && (
        <div className="space-y-2">
          <label className="text-cream text-sm block">
            City
          </label>
          <select
            value={formData.city_locality}
            onChange={(e) => handleInputChange("city_locality", e.target.value)}
            className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple"
          >
            <option value="">Select City</option>
            {cities.map(city => (
              <option key={city.name} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Manual City Input (fallback) */}
      {(!formData.country_code || !formData.region_code || cities.length === 0) && (
        <div className="space-y-2">
          <label className="text-cream text-sm block">
            City
          </label>
          <input
            type="text"
            value={formData.city_locality}
            onChange={(e) => handleInputChange("city_locality", e.target.value)}
            placeholder="Enter city name"
            className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
          />
        </div>
      )}

      {/* Sublocality (optional) */}
      <div className={`space-y-2 transition-opacity duration-200 ${formData.city_locality ? 'opacity-100' : 'opacity-50'}`}>
        <label className="text-cream text-sm block">
          Neighborhood/District <span className="text-cream/50">(optional)</span>
        </label>
        <input
          type="text"
          value={formData.city_sublocality}
          onChange={(e) => handleInputChange("city_sublocality", e.target.value)}
          placeholder="e.g., Downtown, Midtown"
          disabled={!formData.city_locality && !geocodingLoading}
          className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  )
}

export default LocationPicker
